import { useState, useEffect } from "react";
import Hero from "@/components/Hero";
import FinalCTA from "@/components/FinalCTA";
import { Phone, Mail, MapPin, Shield, Check, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const FORMSPREE_URL = "https://formspree.io/f/xeewojqa";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+]?[0-9\s().-]{6,20}$/;

const initial = { nome: "", cognome: "", telefono: "", email: "", messaggio: "", privacy: false, website: "" };

export default function Contatti() {
  const [form, setForm] = useState(initial);
  const [human, setHuman] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Inserisci il nome";
    if (!form.cognome.trim()) e.cognome = "Inserisci il cognome";
    if (!PHONE_RE.test(form.telefono.trim())) e.telefono = "Numero di telefono non valido";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Email non valida";
    if (form.messaggio.trim().length < 5) e.messaggio = "Messaggio troppo breve";
    if (!form.privacy) e.privacy = "Devi accettare l'informativa privacy";
    if (!human) e.human = "Completa la verifica umana";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Save on backend
      await axios.post(`${API}/contact`, {
        nome: form.nome,
        cognome: form.cognome,
        telefono: form.telefono,
        email: form.email,
        messaggio: form.messaggio,
        privacy: form.privacy,
        website: form.website,
      });
      // Send via Formspree
      const fd = new FormData();
      fd.append("nome", form.nome);
      fd.append("cognome", form.cognome);
      fd.append("telefono", form.telefono);
      fd.append("email", form.email);
      fd.append("messaggio", form.messaggio);
      fd.append("_subject", `Nuovo contatto dal sito Drosun Italia — ${form.nome} ${form.cognome}`);
      await fetch(FORMSPREE_URL, { method: "POST", body: fd, headers: { Accept: "application/json" } });

      setDone(true);
      setForm(initial);
      setHuman(false);
      toast.success("Grazie per averci contattato. Ti risponderemo al più presto.");
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova più tardi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Hero
        testId="contatti-hero"
        desktopImg="/images/hero/ContattiHERODesktop.png"
        mobileImg="/images/hero/ContattiHEROMobile.png"
        eyebrow="PARLIAMONE"
        title="Contatta Drosun Italia"
        subtitle="Siamo pronti ad ascoltare il tuo progetto e a offrirti soluzioni personalizzate per progettazione, sviluppo, produzione e packaging industriale."
        ctaLabel="Chiama Ora"
        ctaTo="tel:+393514642407"
      />

      {/* Contact info + map */}
      <section className="py-20 bg-white" data-testid="contatti-info">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="reveal rounded-2xl border border-neutral-200 bg-drosun-light p-8 sm:p-10 shadow-[0_15px_50px_-30px_rgba(0,0,0,0.35)]">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="inline-block w-10 h-[3px] bg-drosun-red" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-drosun-muted">Drosun Italia</span>
            </div>
            <h2 className="font-display uppercase text-3xl sm:text-4xl text-drosun-text">I nostri contatti</h2>
            <ul className="mt-7 space-y-5 text-[16px]">
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white border border-neutral-200 inline-flex items-center justify-center text-drosun-red shrink-0"><MapPin size={20} /></span>
                <div>
                  <div className="font-semibold text-drosun-text">Sede</div>
                  <div className="text-drosun-muted">Drosun — Building 6, TuoFu SongShanHu Technical Park, No.2 Xinghui Road, Dongguan City, Guandong Province, China, 523451</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white border border-neutral-200 inline-flex items-center justify-center text-drosun-red shrink-0"><Phone size={20} /></span>
                <div>
                  <div className="font-semibold text-drosun-text">Europe manager office</div>
                  <a href="tel:+393939881139" className="text-drosun-muted hover:text-drosun-red">+39 3939881139</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white border border-neutral-200 inline-flex items-center justify-center text-drosun-red shrink-0"><Phone size={20} /></span>
                <div>
                  <div className="font-semibold text-drosun-text">Sales office</div>
                  <a href="tel:+393514642407" className="text-drosun-muted hover:text-drosun-red">+39 3514642407</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white border border-neutral-200 inline-flex items-center justify-center text-drosun-red shrink-0"><Mail size={20} /></span>
                <div>
                  <div className="font-semibold text-drosun-text">Europe manager office</div>
                  <a href="mailto:Europe@drosun-eu.com" className="text-drosun-muted hover:text-drosun-red break-all">Europe@drosun-eu.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white border border-neutral-200 inline-flex items-center justify-center text-drosun-red shrink-0"><Mail size={20} /></span>
                <div>
                  <div className="font-semibold text-drosun-text">Sales office</div>
                  <a href="mailto:Sales@drosun-eu.com" className="text-drosun-muted hover:text-drosun-red break-all">Sales@drosun-eu.com</a>
                </div>
              </li>
            </ul>
            <a
              href="tel:+393514642407"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 bg-drosun-red hover:bg-drosun-redHover text-white text-sm font-bold uppercase tracking-[0.12em] rounded-md transition-colors"
              data-testid="contatti-call-button"
            >
              <Phone size={18} /> Chiama Ora
            </a>
          </div>

          {/* Map */}
          <div className="reveal rounded-2xl overflow-hidden border border-neutral-200 shadow-[0_15px_50px_-30px_rgba(0,0,0,0.35)] min-h-[420px]" data-testid="contatti-map">
            <iframe
              title="Mappa Drosun Italia"
              src="https://www.google.com/maps?q=Building+6,+TuoFu+SongShanHu+Technical+Park,+No.2+Xinghui+Road,+Dongguan+City,+Guandong+Province,+China,+523451&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: 420 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-drosun-light" data-testid="contatti-form-section">
        <div className="max-w-[920px] mx-auto px-5 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="inline-block w-10 h-[3px] bg-drosun-red" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-drosun-muted">Scrivici</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl text-drosun-text">Parliamo del tuo prossimo progetto</h2>
            <p className="mt-4 text-[17px] text-drosun-muted">Contatta Drosun Italia per ricevere informazioni sui nostri servizi di progettazione, sviluppo, produzione e soluzioni personalizzate.</p>
          </div>

          {done ? (
            <div className="mt-10 rounded-2xl bg-white border border-emerald-200 p-10 text-center shadow-[0_15px_50px_-30px_rgba(0,0,0,0.35)]" data-testid="contatti-success">
              <span className="inline-flex w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mx-auto"><Check size={28} /></span>
              <h3 className="mt-4 font-display text-2xl uppercase text-drosun-text">Richiesta inviata</h3>
              <p className="mt-2 text-drosun-muted">Grazie per averci contattato. Ti risponderemo nel più breve tempo possibile.</p>
              <button onClick={() => setDone(false)} className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-drosun-navy text-white text-sm font-bold uppercase tracking-wide rounded-md hover:bg-drosun-steel transition-colors">Invia un altro messaggio</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="mt-10 rounded-2xl bg-white border border-neutral-200 p-7 sm:p-10 shadow-[0_15px_50px_-30px_rgba(0,0,0,0.35)] grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contatti-form">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange}
                className="hidden"
                aria-hidden="true"
              />
              <Field label="Nome *" name="nome" value={form.nome} onChange={onChange} error={errors.nome} testid="form-nome" />
              <Field label="Cognome *" name="cognome" value={form.cognome} onChange={onChange} error={errors.cognome} testid="form-cognome" />
              <Field label="Telefono *" name="telefono" type="tel" value={form.telefono} onChange={onChange} error={errors.telefono} testid="form-telefono" />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} error={errors.email} testid="form-email" />
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-drosun-text mb-2" htmlFor="messaggio">Messaggio *</label>
                <textarea
                  id="messaggio" name="messaggio" rows={5} value={form.messaggio} onChange={onChange}
                  className={`w-full rounded-md border bg-white px-4 py-3 text-drosun-text placeholder:text-neutral-400 focus:outline-none focus:ring-2 ${errors.messaggio ? "border-red-400 focus:ring-red-300" : "border-neutral-300 focus:ring-drosun-red"}`}
                  placeholder="Raccontaci il tuo progetto…"
                  data-testid="form-messaggio"
                />
                {errors.messaggio && <p className="mt-1 text-sm text-red-600">{errors.messaggio}</p>}
              </div>

              <div className="sm:col-span-2 flex items-start gap-3">
                <input id="privacy" type="checkbox" name="privacy" checked={form.privacy} onChange={onChange} className="mt-1 w-5 h-5 accent-[#E53935]" data-testid="form-privacy" />
                <label htmlFor="privacy" className="text-sm text-drosun-text">
                  Ho letto e accetto l'
                  <button
                    type="button"
                    onClick={() => setShowPrivacy(true)}
                    className="text-drosun-red hover:underline font-semibold"
                    data-testid="form-privacy-link"
                  >Informativa Privacy</button>.
                </label>
              </div>
              {errors.privacy && <p className="sm:col-span-2 -mt-3 text-sm text-red-600">{errors.privacy}</p>}

              {/* Human verification */}
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={() => setHuman((v) => !v)}
                  className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border transition-colors ${human ? "border-emerald-400 bg-emerald-50" : "border-neutral-300 bg-drosun-light hover:border-drosun-red"}`}
                  data-testid="form-human-verify"
                >
                  <span className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-full inline-flex items-center justify-center ${human ? "bg-emerald-500 text-white" : "bg-white border border-neutral-300 text-drosun-navy"}`}>
                      {human ? <Check size={18} /> : <Shield size={18} />}
                    </span>
                    <span className="text-left">
                      <span className="block font-semibold text-drosun-text">{human ? "Verificato" : "Verifica umana"}</span>
                      <span className="block text-sm text-drosun-muted">{human ? "Grazie, sei stato verificato." : "Clicca per confermare che non sei un bot."}</span>
                    </span>
                  </span>
                  <span className={`text-xs font-bold uppercase tracking-wide ${human ? "text-emerald-600" : "text-drosun-red"}`}>{human ? "OK" : "Conferma"}</span>
                </button>
                {errors.human && <p className="mt-2 text-sm text-red-600">{errors.human}</p>}
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-drosun-red hover:bg-drosun-redHover disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-[0.14em] rounded-md transition-colors"
                  data-testid="form-submit"
                >
                  {submitting ? "Invio in corso…" : "Invia Richiesta"} <ArrowRight size={18} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <FinalCTA
        testId="contatti-final-cta"
        title="Parliamo del tuo prossimo progetto"
        subtitle="Contatta Drosun Italia per ricevere informazioni sui nostri servizi di progettazione, sviluppo, produzione e soluzioni personalizzate."
        primary={{ label: "Richiedi Informazioni", to: "/contatti" }}
        secondary={null}
      />

      <PrivacyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </>
  );
}

function Field({ label, name, type = "text", value, onChange, error, testid }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-drosun-text mb-2" htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className={`w-full rounded-md border bg-white px-4 py-3 text-drosun-text placeholder:text-neutral-400 focus:outline-none focus:ring-2 ${error ? "border-red-400 focus:ring-red-300" : "border-neutral-300 focus:ring-drosun-red"}`}
        autoComplete="off"
        data-testid={testid}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function PrivacyModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="privacy-modal"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[88vh] sm:max-h-[80vh] sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-drosun-navy text-white">
          <h3 id="privacy-modal-title" className="font-display uppercase text-xl tracking-wide">Informativa Privacy</h3>
          <button onClick={onClose} aria-label="Chiudi" className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="privacy-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-6 text-[15px] leading-relaxed text-drosun-text space-y-4">
          <p className="text-drosun-muted text-sm">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>

          <h4 className="font-display uppercase text-lg text-drosun-text">Titolare del trattamento</h4>
          <p>Drosun — Building 6, TuoFu SongShanHu Technical Park, No.2 Xinghui Road, Dongguan City, Guandong Province, China, 523451. Email: <a href="mailto:Europe@drosun-eu.com" className="text-drosun-red">Europe@drosun-eu.com</a>.</p>

          <h4 className="font-display uppercase text-lg text-drosun-text">Tipi di dati raccolti</h4>
          <p>Tramite il modulo contatti raccogliamo: nome, cognome, telefono, email e il contenuto del messaggio inviato. I dati sono trattati al solo scopo di rispondere alla richiesta dell'utente.</p>

          <h4 className="font-display uppercase text-lg text-drosun-text">Base giuridica e finalità</h4>
          <p>Il trattamento si basa sul consenso espresso dell'utente (art. 6.1.a GDPR) e sull'esecuzione di misure precontrattuali (art. 6.1.b GDPR). I dati non vengono utilizzati per profilazione o marketing.</p>

          <h4 className="font-display uppercase text-lg text-drosun-text">Conservazione dei dati</h4>
          <p>I dati sono conservati per il tempo strettamente necessario a evadere la richiesta e comunque non oltre 24 mesi.</p>

          <h4 className="font-display uppercase text-lg text-drosun-text">Diritti dell'utente</h4>
          <p>L'utente può in qualsiasi momento esercitare i diritti previsti dagli artt. 15-22 GDPR (accesso, rettifica, cancellazione, limitazione, portabilità e opposizione) scrivendo a <a href="mailto:Europe@drosun-eu.com" className="text-drosun-red">Europe@drosun-eu.com</a>.</p>
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 bg-drosun-light flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-drosun-red hover:bg-drosun-redHover text-white text-sm font-bold uppercase tracking-wide rounded-md transition-colors"
            data-testid="privacy-modal-ok"
          >Ho capito</button>
        </div>
      </div>
    </div>
  );
}
